import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, ErrorMessage, getIn, useField } from 'formik';
import styled, { withTheme } from 'styled-components';
import { withValue, hook } from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';

import { VisuallyHidden, Box } from '../Boneyard';
import { FIELD_ERROR_MESSAGE } from '../../../automationHooks.json';
import { DOB_YEAR_FIELD } from '../../pageComponents/travelersPage/travelersForm/partsNames';

const propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  withFieldError: PropTypes.bool,
  withErrorMessage: PropTypes.bool,
  fieldLevelValidation: PropTypes.func,
  resetOnUnmount: PropTypes.bool,
  theme: PropTypes.object.isRequired,
  errorfieldName: PropTypes.string,
  label: PropTypes.string,
};

const defaultProps = {
  withFieldError: false,
  withErrorMessage: true,
  onChange: Function.prototype,
  onBlur: Function.prototype,
};

const ErrorText = styled.em.attrs({ 'aria-live': 'polite', role: 'alert' })`
  font-size: ${props => props.theme.toRem(12)};
  color: ${props => props.theme.colors.common.alertRed};
  padding-top: ${props => props.theme.spacing(2)};
`;

export const Error = React.forwardRef(
  ({ automationHook, id, label, msg }, ref) => {
    return (
      <ErrorText {...hook(automationHook)} id={id} ref={ref}>
        {label && <VisuallyHidden>{label}</VisuallyHidden>}
        {msg}
      </ErrorText>
    );
  }
);

Error.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  automationHook: PropTypes.string,
  theme: PropTypes.object,
  id: PropTypes.string,
  msg: PropTypes.string,
};

const findFirstErrorString = (errors, path = '') => {
  for (const [fieldName, fieldError] of Object.entries(errors)) {
    const errorPath = path ? `${path}.${fieldName}` : fieldName;
    if (typeof fieldError === 'string') {
      return errorPath;
    }

    if (typeof fieldError === 'object') {
      return findFirstErrorString(fieldError, errorPath);
    }

    if (Array.isArray(fieldError)) {
      for (const travelerErrors of fieldError) {
        return findFirstErrorString(travelerErrors, errorPath);
      }
    }
  }
};

const FieldHandler = React.memo(
  ({
    name,
    children,
    onChange,
    onBlur,
    withFieldError,
    withErrorMessage,
    resetOnUnmount = false,
    fieldLevelValidation,
    errorfieldName,
    label,
  }) => {
    const {
      values,
      handleChange,
      handleBlur,
      setFieldValue,
      errors,
      isSubmitting,
      isValidating,
    } = useFormikContext();
    const errorElementRef = useRef();

    useEffect(() => {
      // TODO: Scrolling to SSRs errors does not work because they don't have a specified initial value:
      // https://github.com/jaredpalmer/formik/issues/445#issuecomment-366952762
      const scrollToError = () => {
        return errorElementRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      };
      if (Object.keys(errors).length && isSubmitting && !isValidating) {
        const firstErrorField = findFirstErrorString(errors);
        if (firstErrorField === name) {
          scrollToError();
        }
      }
    }, [errors, isSubmitting, isValidating, name]);

    useEffect(() => {
      if (resetOnUnmount) {
        return () => setFieldValue(name, undefined);
      }
    }, [setFieldValue, name, resetOnUnmount]);

    const handleValueChange = useCallback(
      value => {
        const prevValue = getIn(values, name);

        // call formik handleChange method
        handleChange({
          target: {
            value,
            name,
          },
        });
        // call the component's prop onChange
        onChange(value, prevValue);
      },
      [handleChange, name, onChange, values]
    );

    const handleValueBlur = useCallback(
      value => {
        const prevValue = getIn(values, name);

        // call formik handleBlur method
        handleBlur({
          target: {
            value,
            name,
          },
        });
        // call the component's prop onBlur
        setTimeout(() => onBlur(value, prevValue), 0);

        fieldLevelValidation && fieldLevelValidation(value);
      },
      [handleBlur, onBlur, fieldLevelValidation, name, values]
    );

    const [{ value: formikValue, ...field }, meta] = useField({
      name,
      validate: fieldLevelValidation,
    });

    const childrenArray = React.Children.toArray(children);
    const restOfChildren = childrenArray.splice(1, childrenArray.length);
    const value =
      childrenArray[0].props.autoSelectedDefaultValue !== undefined &&
      !formikValue
        ? childrenArray[0].props.autoSelectedDefaultValue
        : formikValue;

    const trackEvent = label => {
      tracking.trackEvent('client_side_error', label);
    };

    const trackError = msg => {
      const splittedFieldName = name.split('.');
      const msgFieldName = splittedFieldName[splittedFieldName.length - 1];
      if (
        msgFieldName === DOB_YEAR_FIELD &&
        ErrorMessage(msgFieldName)._owner.pendingProps.formik.submitCount
      ) {
        trackEvent('Traveler - DOB Not Entered');
      }

      if (
        msgFieldName === DOB_YEAR_FIELD &&
        msg === 'Please enter a valid Year'
      ) {
        trackEvent('Traveler - DOB Invalid Year');
      }
    };

    return (
      <Box d="block">
        {React.cloneElement(childrenArray[0], {
          ...field,
          value,
          errorId: errorfieldName,
          onChange: handleValueChange,
          onBlur: handleValueBlur,
          hasError: (!!meta.error && !!meta.touched) || withFieldError,
        })}
        {restOfChildren}
        {withErrorMessage ? (
          <ErrorMessage name={name}>
            {msg => {
              trackError(msg);
              return (
                <Error
                  automationHook={withValue(FIELD_ERROR_MESSAGE.hook, name)}
                  id={errorfieldName}
                  label={label}
                  msg={msg}
                  ref={errorElementRef}
                />
              );
            }}
          </ErrorMessage>
        ) : null}
      </Box>
    );
  }
);

FieldHandler.propTypes = propTypes;
FieldHandler.defaultProps = defaultProps;
FieldHandler.displayName = 'FieldHandler';

export default withTheme(FieldHandler);
